import { LitElement, html } from 'lit';
import * as Sentry from '@sentry/browser';
import { fetchPrintPrice, addPrintToQuote } from '../../services/apis/print-api-service.js';
import { parseVolumeString, formatDateForPrintApi } from '../../utils/format-utils.js';
import { NotificationBus } from '../../services/notification-bus.js';
import './print-upsell-display.js';

/**
 * Volume limit for automatic pricing
 * Quantities above this require manual quoting
 * Configurable via VITE_PRINT_VOLUME_THRESHOLD environment variable
 */
const VOLUME_LIMIT = parseInt(import.meta.env.VITE_PRINT_VOLUME_THRESHOLD, 10) || 100000;

/**
 * @component print-upsell-card
 * @category Interactive
 * @description Container component for print service upsell feature.
 * Handles pricing API calls, state management, and quote updates.
 *
 * @fires {CustomEvent} reload-quote - Request parent to reload quote data
 *
 * @example
 * <print-upsell-card
 *   .quoteData="${quoteData}"
 *   .quoteReference="${reference}"
 *   @reload-quote="${handleReload}"
 * ></print-upsell-card>
 */
class PrintUpsellCard extends LitElement {
    static properties = {
        quoteData: { type: Object },
        quoteReference: { type: String },
        // Internal state
        loading: { type: Boolean, state: true },
        error: { type: Boolean, state: true },
        price: { type: String, state: true },
        adding: { type: Boolean, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteData = null;
        this.quoteReference = '';
        this.loading = false;
        this.error = false;
        this.price = null;
        this.adding = false;
    }

    connectedCallback() {
        super.connectedCallback();

        // Fetch pricing on mount if upsell should show and doesn't require manual quote
        if (this.shouldShowUpsell() && this.volumeNumber > 0 && !this.requiresManualQuote()) {
            this._fetchPrintPrice();
        }

        // Track upsell display
        if (this.shouldShowUpsell()) {
            this._trackAnalytics('displayed', {
                volume: this.letterboxVolume,
                manualQuoteRequired: this.requiresManualQuote(),
            });
        }
    }

    /**
     * Get the mailers volume string from quote data
     */
    get letterboxVolume() {
        return this.quoteData?.totQty || '0';
    }

    /**
     * Get the numeric volume value
     */
    get volumeNumber() {
        try {
            return parseVolumeString(this.letterboxVolume);
        } catch {
            return 0;
        }
    }

    /**
     * Get the account manager (RM) contact name
     */
    get rmContactName() {
        return this.quoteData?.rmContact || 'REACH Team';
    }

    /**
     * Get the account manager (RM) contact email
     */
    get rmContactEmail() {
        return this.quoteData?.rmContactEmail || 'info@reach.nz';
    }

    /**
     * Determine if print upsell should be displayed
     * Requires: hasLetterbox=Y AND hasPrint=N
     */
    shouldShowUpsell() {
        if (!this.quoteData) return false;

        const hasLetterbox = this.quoteData.hasLetterbox === 'Y';
        const hasPrint = this.quoteData.hasPrint === 'Y';

        return hasLetterbox && !hasPrint;
    }

    /**
     * Check if volume exceeds limit requiring manual quote
     */
    requiresManualQuote() {
        return this.volumeNumber > VOLUME_LIMIT;
    }

    /**
     * Build print specifications for API call
     */
    _buildPrintSpecs() {
        const startDate = this.quoteData?.startDate
            ? formatDateForPrintApi(this.quoteData.startDate)
            : null;

        return {
            paperSize: 'A5',
            pagination: 'Double Sided',
            colours: 'CMYK',
            gsm: '150gsm',
            qty: this.volumeNumber,
            startDate,
        };
    }

    /**
     * Track analytics event to Sentry
     */
    _trackAnalytics(event, data = {}) {
        Sentry.addBreadcrumb({
            category: 'print-upsell',
            message: `Print upsell ${event}`,
            data: {
                quoteReference: this.quoteReference,
                ...data,
            },
        });

        if (import.meta.env.DEV) {
            console.log(`[PrintUpsell] Analytics: ${event}`, data);
        }
    }

    /**
     * Fetch print pricing from API
     */
    async _fetchPrintPrice() {
        this.loading = true;
        this.error = false;
        this.price = null;

        try {
            const specs = this._buildPrintSpecs();
            const price = await fetchPrintPrice(specs);

            if (price) {
                this.price = price;
                this._trackAnalytics('pricing-loaded', { price });
            } else {
                this.error = true;
                this._trackAnalytics('pricing-failed', { reason: 'null-response' });
            }
        } catch (err) {
            this.error = true;
            console.error('[PrintUpsell] Failed to fetch pricing:', err);
            this._trackAnalytics('pricing-failed', { reason: err.message });
        } finally {
            this.loading = false;
        }
    }

    /**
     * Handle add to booking button click
     */
    async _handleAddClick() {
        this.adding = true;
        this._trackAnalytics('add-clicked', { price: this.price });

        try {
            const specs = this._buildPrintSpecs();
            await addPrintToQuote(this.quoteReference, specs);

            this._trackAnalytics('print-added', { price: this.price });

            // Emit reload event for parent to refresh quote data
            this.dispatchEvent(
                new CustomEvent('reload-quote', {
                    bubbles: true,
                    composed: true,
                })
            );

            NotificationBus.success('Print service added to quote', 'PrintUpsell');
        } catch (err) {
            console.error('[PrintUpsell] Failed to add print:', err);
            this._trackAnalytics('add-failed', { reason: err.message });

            NotificationBus.error(
                'Failed to add print service. Please try again or contact support.',
                'PrintUpsell'
            );

            Sentry.captureException(err, {
                tags: { feature: 'print-upsell', action: 'add-print' },
            });
        } finally {
            this.adding = false;
        }
    }

    /**
     * Handle contact us button click
     */
    _handleContactClick() {
        const eventType = this.requiresManualQuote() ? 'contact-manual-quote' : 'contact-pricing';
        this._trackAnalytics(eventType, { volume: this.letterboxVolume });
    }

    render() {
        if (!this.shouldShowUpsell()) {
            return html``;
        }

        return html`
            <print-upsell-display
                volume="${this.letterboxVolume}"
                price="${this.price || ''}"
                ?loading="${this.loading}"
                ?error="${this.error}"
                ?manualQuoteRequired="${this.requiresManualQuote()}"
                ?adding="${this.adding}"
                rmContactName="${this.rmContactName}"
                rmContactEmail="${this.rmContactEmail}"
                quoteReference="${this.quoteReference}"
                @add-click="${this._handleAddClick}"
                @contact-click="${this._handleContactClick}"
            ></print-upsell-display>
        `;
    }
}

customElements.define('print-upsell-card', PrintUpsellCard);
